/**
 * Toast notifications. Wrap the app in <ToastProvider> (the Foundation already
 * does this in App.tsx) and call `useToast()` to push messages:
 *   const toast = useToast();
 *   toast.success('Saved');
 *   toast.error('Could not save', 'Please retry.');
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
}

export interface ToastApi {
  show: (
    tone: ToastTone,
    title: string,
    description?: string,
    durationMs?: number,
  ) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const toneStyles: Record<ToastTone, string> = {
  success: 'border-l-success',
  error: 'border-l-danger',
  info: 'border-l-info',
  warning: 'border-l-warning',
};

const toneIcon: Record<ToastTone, string> = {
  success: '✓',
  error: '✕',
  info: 'i',
  warning: '!',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback<ToastApi['show']>(
    (tone, title, description, durationMs = 4500) => {
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      setToasts((prev) => [...prev, { id, tone, title, description }]);
      const timer = setTimeout(() => dismiss(id), durationMs);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (title, description) => show('success', title, description),
      error: (title, description) => show('error', title, description),
      info: (title, description) => show('info', title, description),
      warning: (title, description) => show('warning', title, description),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
          aria-live="polite"
          aria-atomic="false"
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              className={cn(
                'pointer-events-auto flex items-start gap-3 rounded-xl border border-l-4 border-surface-border bg-surface p-3.5 shadow-card dark:border-surface-dark-border dark:bg-surface-dark-muted',
                toneStyles[t.tone],
              )}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white',
                  t.tone === 'success' && 'bg-success',
                  t.tone === 'error' && 'bg-danger',
                  t.tone === 'info' && 'bg-info',
                  t.tone === 'warning' && 'bg-warning text-surface-dark',
                )}
                aria-hidden="true"
              >
                {toneIcon[t.tone]}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-surface-dark dark:text-surface">
                  {t.title}
                </p>
                {t.description && (
                  <p className="mt-0.5 text-sm text-surface-dark/60 dark:text-surface/60">
                    {t.description}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
                className="shrink-0 rounded-md p-1 text-surface-dark/40 hover:text-surface-dark dark:text-surface/40 dark:hover:text-surface"
              >
                ✕
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a <ToastProvider>.');
  return ctx;
}
